/**
 * Verdeling per kostenplaats — read-only rollup panel.
 *
 * Shows, for the live verdeling-draft, how much budget lands on each unit
 * (kostenplaats). It reads the same `SplitItemPayload[]` both editing views
 * produce, so it stays correct regardless of whether the user is working
 * label-first or kostenplaats-first. Toggled on next to the canvas so you can
 * see the per-unit picture while you edit.
 */

import React, { useMemo } from "react";
import { Cube, ListBullets, Warning } from "@phosphor-icons/react";
import { UnitDTO } from "shared/types/unit";
import {
  aggregatePerUnit,
  formatCurrency,
  getUnitSurface,
  SplitItemPayload,
} from "./budgetSplitShared";

interface UnitVerdelingSummaryProps {
  items: SplitItemPayload[];
  unitsById: Map<string, UnitDTO>;
}

export const UnitVerdelingSummary: React.FC<UnitVerdelingSummaryProps> = ({
  items,
  unitsById,
}) => {
  const { rows, unassigned, total } = useMemo(
    () => aggregatePerUnit(items),
    [items],
  );

  const maxAmount = rows.length > 0 ? rows[0].amount : 0;
  const isEmpty = rows.length === 0 && unassigned === 0;

  return (
    <aside className="flex flex-col gap-2 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50 p-3">
      <div className="text-neutral-secondary-mid flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
        <ListBullets size={12} weight="duotone" />
        Verdeling per kostenplaats
      </div>

      {isEmpty ? (
        <div className="text-neutral-secondary-mid flex flex-1 flex-col items-center justify-center gap-2 px-2 text-center text-xs">
          <Cube size={28} weight="duotone" className="text-neutral-300" />
          Nog niets verdeeld. Voeg budgetlijnen met een bedrag en een
          kostenplaats toe om de verdeling hier te zien.
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between rounded-md border border-neutral-200 bg-white px-2.5 py-1.5">
            <span className="text-neutral-secondary-mid text-[11px]">
              Totaal in concept
            </span>
            <span className="text-neutral-primary text-sm font-semibold tabular-nums">
              {formatCurrency(total)}
            </span>
          </div>

          <div className="flex-1 space-y-1.5 overflow-y-auto pr-1">
            {rows.map((row) => {
              const unit = unitsById.get(row.unitId);
              const surface = getUnitSurface(unit);
              const pctOfTotal = total > 0 ? (row.amount / total) * 100 : 0;
              const barPct = maxAmount > 0 ? (row.amount / maxAmount) * 100 : 0;
              const perM2 =
                surface && surface > 0 ? Math.round(row.amount / surface) : null;
              return (
                <div
                  key={row.unitId}
                  className="rounded-md border border-neutral-200 bg-white p-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-moss-primary inline-flex items-center gap-1 text-xs font-semibold">
                      <Cube size={11} weight="duotone" />
                      <span className="tabular-nums">{row.unitId}</span>
                      {surface !== null && (
                        <span className="text-neutral-secondary-mid text-[10px] font-normal tabular-nums">
                          · {surface} m²
                        </span>
                      )}
                    </span>
                    <span className="text-neutral-primary text-xs font-semibold tabular-nums">
                      {formatCurrency(row.amount)}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200">
                    <div
                      className="bg-moss-primary h-full rounded-full"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  <div className="text-neutral-secondary-mid mt-1 flex items-center justify-between text-[10px] tabular-nums">
                    <span>{pctOfTotal.toFixed(1)}% van concept</span>
                    {perM2 !== null && <span>{formatCurrency(perM2)}/m²</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {unassigned > 0 && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5">
              <span className="text-amber-700 inline-flex items-center gap-1 text-[11px] font-medium">
                <Warning size={12} weight="duotone" />
                Geen kostenplaats
              </span>
              <span className="text-amber-700 text-xs font-semibold tabular-nums">
                {formatCurrency(unassigned)}
              </span>
            </div>
          )}
        </>
      )}
    </aside>
  );
};

export default UnitVerdelingSummary;
